import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import {
  ChevronDown, ChevronRight, LogOut, Plus,
  Menu, X, ArrowLeft, ArrowRight, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { GlobalSearch } from '@/components/GlobalSearch';
import { OnboardingTour } from '@/components/OnboardingTour';
import { QuickCaptureModal } from '@/components/QuickCaptureModal';

const navSections = [
  {
    title: null,
    items: [
      { to: '/home', emoji: '\u{1F3E0}', label: 'Home', hasBadge: 'home' },
      { to: '/command', emoji: '\u{1F4AC}', label: 'Centro di Comando' },
      { to: '/board', emoji: '\u25A6', label: 'Board operativa', hasBadge: 'board' },
      { to: '/timeline', emoji: '\u{1F4CA}', label: 'Progetto & Timeline' },
    ]
  },
  {
    title: 'CONTENUTI',
    items: [
      { to: '/content-studio', emoji: '\u270F\uFE0F', label: 'Content Studio' },
      { to: '/research', emoji: '\u{1F50D}', label: 'Deep Research' },
      { to: '/planner', emoji: '\u{1F4C5}', label: 'Planner' },
      { to: '/approval', emoji: '\u2713', label: 'Approvazione', hasBadge: 'approval' },
    ]
  },
  {
    title: 'GESTIONE',
    items: [
      { to: '/contacts', emoji: '\u{1F464}', label: 'Contatti & CRM' },
      { to: '/events', emoji: '\u{1F4C6}', label: 'Eventi' },
      { to: '/sales', emoji: '\u{1F4B0}', label: 'Pipeline vendite' },
    ]
  },
  {
    title: 'SISTEMA',
    collapsible: true,
    items: [
      { to: '/workspace', emoji: '\u{1F3E2}', label: 'Workspace' },
      { to: '/agents', emoji: '\u{1F916}', label: 'Team Agenti' },
      { to: '/settings', emoji: '\u2699\uFE0F', label: 'Impostazioni' },
    ]
  },
];

export default function Layout({ children }) {
  const { user, organization, projects, currentProject, logout, switchProject } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showTour, setShowTour] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [systemOpen, setSystemOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [homeBadge, setHomeBadge] = useState(0);
  const [boardBadge, setBoardBadge] = useState(0);
  const [brandLogoUrl, setBrandLogoUrl] = useState(null);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await api.get('/approval-queue?status=pending');
        setPendingCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch {}
    };
    const fetchNotifs = async () => {
      try {
        const [notifRes, countRes] = await Promise.all([
          api.get('/notifications?limit=15'),
          api.get('/notifications/unread-count'),
        ]);
        setNotifications(notifRes.data?.notifications || []);
        const uc = countRes.data?.unread || 0;
        setUnreadCount(uc);
        setHomeBadge(uc > 0 ? Math.min(uc, 9) : 0);
      } catch {}
    };
    const fetchBoards = async () => {
      try {
        const res = await api.get('/process/boards');
        const running = (res.data || []).filter(b => ['running', 'paused'].includes(b.status));
        setBoardBadge(running.length);
      } catch {}
    };
    fetchPending();
    fetchNotifs();
    fetchBoards();
    const fetchBrand = async () => {
      try {
        const res = await api.get('/company/profile');
        setBrandLogoUrl(res.data?.brand_logo_url || null);
      } catch {}
    };
    fetchBrand();
    const interval = setInterval(() => { fetchPending(); fetchNotifs(); fetchBoards(); }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('hai_tour_done')) setShowTour(true);
  }, []);

  const getBadge = (item) => {
    if (item.hasBadge === 'approval' && pendingCount > 0) return pendingCount;
    if (item.hasBadge === 'home' && homeBadge > 0) return homeBadge;
    if (item.hasBadge === 'board' && boardBadge > 0) return boardBadge;
    return 0;
  };

  const userInitials = user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';

  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      {/* Sidebar v0 style */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[220px] flex flex-col transition-transform duration-300 lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: '#0C1729' }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between">
            <a href="https://human-aimpact.ch" target="_blank" rel="noopener noreferrer">
              {brandLogoUrl ? (
                <img src={brandLogoUrl} alt="Brand" className="h-8 max-w-[160px] object-contain" />
              ) : (
                <div className="text-[18px]">
                  <span className="font-bold text-white">HumanAI</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>mpact</span>
                </div>
              )}
            </a>
            <button className="lg:hidden text-white/70" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            piattaforma operativa
          </div>
        </div>

        {/* Project Switcher */}
        <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center justify-between text-[12px] px-2 py-1.5 rounded-md transition-colors"
                style={{ color: 'rgba(255,255,255,0.7)' }}
                data-testid="project-switcher">
                <span className="truncate">{currentProject?.name || 'Seleziona progetto'}</span>
                <ChevronDown className="w-3.5 h-3.5 ml-1 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {projects.map(p => (
                <DropdownMenuItem key={p.id} onClick={() => switchProject(p)} data-testid={`project-${p.id}`}>
                  {p.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/onboarding')} data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuovo Progetto
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Quick Capture Button */}
        <div className="p-4 pt-3">
          <button
            onClick={() => setCaptureOpen(true)}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 transition-all"
            style={{ border: '1px dashed rgba(255,255,255,0.25)' }}
            data-testid="quick-capture-btn"
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            <span className="text-[14px] font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>+</span>
            <span className="text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>Cattura idea...</span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto" data-testid="nav-sections">
          {navSections.map((section, si) => (
            <div key={si}>
              {section.title && (
                section.collapsible ? (
                  <button
                    onClick={() => setSystemOpen(!systemOpen)}
                    className="flex w-full items-center justify-between px-[14px] pb-[4px] pt-[14px]"
                    style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px' }}
                    data-testid={`section-${section.title.toLowerCase()}`}
                  >
                    <span>{section.title}</span>
                    {systemOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                  </button>
                ) : (
                  <div
                    className="px-[14px] pb-[4px] pt-[14px]"
                    style={{ color: 'rgba(255,255,255,0.45)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1.2px' }}
                    data-testid={`section-${section.title.toLowerCase()}`}
                  >
                    {section.title}
                  </div>
                )
              )}
              {(!section.collapsible || systemOpen) && section.items.map(item => {
                const active = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to + '/'));
                const badge = getBadge(item);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex w-full items-center px-[14px] py-[9px] transition-colors"
                    style={{
                      backgroundColor: active ? '#2563EB' : 'transparent',
                      color: active ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.75)',
                      fontSize: '14px',
                    }}
                    data-testid={`nav-${item.to.slice(1)}`}
                    onClick={() => setSidebarOpen(false)}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.95)'; }}}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.75)'; }}}
                  >
                    <span className="w-[22px] text-center" style={{ marginRight: '10px', fontSize: '15px' }}>{item.emoji}</span>
                    <span className="flex-1 text-left">{item.label}</span>
                    {badge > 0 && (
                      <span className="rounded-[8px] px-[6px] py-[2px] text-[10px] font-bold text-white" style={{ backgroundColor: '#DC2626' }} data-testid={`badge-${item.to.slice(1)}`}>
                        {badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer - User Profile */}
        <button
          onClick={() => navigate('/settings')}
          className="flex w-full items-center gap-3 px-[14px] py-[14px] transition-colors"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
          data-testid="user-profile-sidebar"
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-full text-[13px] font-bold text-white" style={{ backgroundColor: '#2563EB' }}>
            {userInitials}
          </div>
          <div className="flex flex-col text-left">
            <span className="text-[13px] font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>
              {user?.full_name || 'Utente'}
            </span>
            <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {organization?.name || 'HumanAImpact'}
            </span>
          </div>
        </button>
      </aside>

      {/* Overlay mobile */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <header className="h-14 bg-white border-b border-[#E2E8F0] flex items-center justify-between px-5 sticky top-0 z-30" data-testid="app-header">
          <div className="flex items-center gap-2">
            <button className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="sidebar-toggle">
              <Menu className="w-5 h-5 text-[#1A3A5C]" />
            </button>
            <div className="hidden sm:flex items-center gap-0.5">
              <button onClick={() => window.history.back()} className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#64748B] hover:text-[#1A3A5C] transition-colors" data-testid="nav-back">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button onClick={() => window.history.forward()} className="p-1.5 rounded-md hover:bg-[#F1F5F9] text-[#64748B] hover:text-[#1A3A5C] transition-colors" data-testid="nav-forward">
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="hidden sm:block w-72">
              <GlobalSearch />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <div className="relative" data-testid="notification-bell">
              <button onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-md hover:bg-[#F1F5F9] text-[#64748B] hover:text-[#1A3A5C] transition-colors">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[9px] font-bold rounded-full px-0.5" data-testid="unread-badge">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 w-96 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 max-h-[480px] overflow-hidden flex flex-col" data-testid="notification-panel">
                    <div className="p-3 border-b border-[#E2E8F0] flex items-center justify-between flex-shrink-0">
                      <span className="text-xs font-semibold text-[#1A3A5C]">
                        Notifiche {unreadCount > 0 && <span className="text-red-500 ml-1">({unreadCount} nuove)</span>}
                      </span>
                      {unreadCount > 0 && (
                        <button onClick={async () => {
                          await api.put('/notifications/read-all');
                          setUnreadCount(0);
                          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                        }} className="text-[10px] text-[#2E75B6] hover:underline" data-testid="mark-all-read">
                          Segna tutte lette
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {notifications.length === 0 ? (
                        <div className="p-6 text-center text-xs text-[#94A3B8]">Nessuna notifica</div>
                      ) : notifications.map((n) => {
                        const sevColors = { critical: 'border-l-red-500 bg-red-50/50', warning: 'border-l-amber-500 bg-amber-50/30', info: 'border-l-blue-500 bg-blue-50/30' };
                        const sevBg = sevColors[n.severity] || sevColors.info;
                        return (
                          <div key={n.id}
                            className={`px-3 py-2.5 border-b border-[#F1F5F9] last:border-0 border-l-2 ${sevBg} ${!n.read ? 'bg-opacity-100' : 'opacity-60'} hover:opacity-100 transition-opacity cursor-pointer`}
                            onClick={async () => {
                              if (!n.read) {
                                await api.put(`/notifications/${n.id}/read`);
                                setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
                                setUnreadCount(prev => Math.max(0, prev - 1));
                              }
                              if (n.link) { setNotifOpen(false); navigate(n.link); }
                              else { setNotifOpen(false); }
                            }}
                            data-testid={`notif-${n.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-medium text-[#0F172A] leading-tight">{n.title}</p>
                              {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />}
                            </div>
                            <p className="text-[10px] text-[#64748B] mt-1 leading-snug line-clamp-2">{n.message}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] text-[#94A3B8]">
                                {n.source_agent && `${n.source_agent} \u00B7 `}
                                {n.created_at ? new Date(n.created_at).toLocaleString('it-CH', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                              </span>
                              {n.link && <span className="text-[9px] text-[#2E75B6]">Vai \u2192</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative z-50 flex items-center gap-2 text-sm" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-[#2563EB] flex items-center justify-center text-white text-xs font-semibold">
                    {userInitials}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[100]">
                <DropdownMenuItem className="text-xs text-muted-foreground">{user?.email}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main className={`flex-1 overflow-y-auto bg-[#F5F5F5] page-content ${['/command', '/board', '/home'].includes(location.pathname) || location.pathname.startsWith('/board/') ? 'p-0' : 'p-6 md:p-8'}`}>
          {children}
        </main>
      </div>

      {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
      <QuickCaptureModal isOpen={captureOpen} onClose={() => setCaptureOpen(false)} />
    </div>
  );
}
